import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";
import { WYSIWYGChapterEditor } from "@/features/admin/components/wysiwyg";

interface Chapter {
  id: string;
  title: string;
  courseId: string;
  course?: {
    id: string;
    title: string;
  };
}

// Helper to validate ID format (UUID or CUID)
const isValidId = (id: string | undefined): id is string =>
  !!id &&
  // UUID format: 550e8400-e29b-41d4-a716-446655440000
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ||
    // CUID format: cjld2cjxh0000qzrmn831i7rn (starts with 'c', 25+ alphanumeric chars)
    /^c[a-z0-9]{20,}$/i.test(id));

/**
 * Editor WYSIWYG de capítulos (V2).
 * Muestra el player real con edición inline.
 */
export default function ChapterEditorPageV2() {
  const { courseId, id: chapterId } = useParams<{
    courseId: string;
    id: string;
  }>();

  // Validate IDs early
  const hasValidIds = isValidId(chapterId) && isValidId(courseId);

  // Fetch chapter data - only if IDs are valid
  const { data: chapter, isLoading } = useQuery({
    queryKey: ["chapter", chapterId],
    queryFn: async () => {
      if (!isValidId(chapterId)) {
        throw new Error("Invalid chapterId");
      }
      const response = await api.get<ApiResponse<Chapter>>(
        `/chapters/${chapterId}`,
      );
      return response.data.data!;
    },
    enabled: hasValidIds,
  });

  // Show error if IDs are invalid
  if (!hasValidIds) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-lg text-gray-700">
          IDs de capítulo o curso inválidos
        </p>
        <Link to="/admin/courses" className="text-primary-600 hover:underline">
          Volver a cursos
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/admin/courses/${courseId}/chapters`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {chapter?.title || "Cargando..."}
            </h1>
            <p className="text-sm text-gray-500">Editor WYSIWYG</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/admin/courses/${courseId}/chapters/${chapterId}/edit-classic`}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            Editor clásico
          </Link>
        </div>
      </header>

      {/* Editor */}
      <main className="flex-1 overflow-hidden">
        <WYSIWYGChapterEditor
          chapterId={chapterId}
          courseId={courseId}
          chapterTitle={chapter?.title}
        />
      </main>
    </div>
  );
}
